import type { LegacyUser } from '../services/api';

/**
 * RBAC multi-perfil do painel (Fase 26).
 *
 * Um SaasUser pode acumular vários perfis (`roles[]`) — ex: financeiro +
 * vendedor. `role` (singular) é o espelho legado do perfil principal.
 *
 * Perfis novos: admin, monitor, financeiro, vendedor, gestao_vendas,
 * gestao_projetos. Legados (pré-Fase 26): operator, viewer — tratados como
 * acesso amplo ao painel (comportamento anterior preservado).
 */
export type SaasRole =
  | 'admin'
  | 'monitor'
  | 'financeiro'
  | 'vendedor'
  | 'gestao_vendas'
  | 'gestao_projetos'
  | 'operator'
  | 'viewer';

export const ROLE_LABEL: Record<SaasRole, string> = {
  admin: 'Admin',
  monitor: 'Monitor',
  financeiro: 'Financeiro',
  vendedor: 'Vendedor',
  gestao_vendas: 'Gestão de vendas',
  gestao_projetos: 'Gestão de projetos',
  operator: 'Operator (legado)',
  viewer: 'Viewer (legado)',
};

/** Perfis selecionáveis no CRUD (legados ficam só como leitura). */
export const ASSIGNABLE_ROLES: SaasRole[] = [
  'admin',
  'monitor',
  'financeiro',
  'vendedor',
  'gestao_vendas',
  'gestao_projetos',
];

/** Perfis efetivos do usuário logado (roles[] com fallback pro role). */
export function userRoles(user: LegacyUser | null): SaasRole[] {
  if (!user) return [];
  const r = (user as { roles?: unknown }).roles;
  if (Array.isArray(r) && r.length > 0) return r as SaasRole[];
  return user.role ? [user.role as SaasRole] : [];
}

/**
 * true se o usuário tem ALGUM dos perfis. Admin sempre passa; operator e
 * viewer (legados) também passam — eram usuários de painel completo.
 */
export function hasRole(user: LegacyUser | null, ...roles: SaasRole[]): boolean {
  const mine = userRoles(user);
  if (mine.includes('admin') || mine.includes('operator') || mine.includes('viewer')) {
    return true;
  }
  return mine.some((r) => roles.includes(r));
}

/** true se tem acesso TOTAL ao painel (admin ou legados). */
export function isFullAccess(user: LegacyUser | null): boolean {
  const mine = userRoles(user);
  return mine.includes('admin') || mine.includes('operator') || mine.includes('viewer');
}
